import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TrialTrackerTrial,
  TrialTrackerAssumptions,
  computeTrackerMilestones,
  phaseShortLabel,
  registryForNctId,
  statusColorClasses,
  DESIGNATION_META,
} from '../../utils/trialTrackerTimeline';

interface Props {
  trials: TrialTrackerTrial[];
  showGroupHeaders: boolean;
  assumptions: TrialTrackerAssumptions;
  onBumpAssumption: (key: keyof TrialTrackerAssumptions, delta: number) => void;
  onSelect: (nctId: string) => void;
  priorityOverrides: Record<string, boolean>;
  onTogglePriority: (nctId: string, e: React.MouseEvent) => void;
  onFilterBySponsor?: (sponsor: string) => void;
}

const PX_PER_MONTH_DEFAULT = 22;
const LABEL_COL_WIDTH = 260;

function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + (b.getDate() - a.getDate()) / 30;
}

const LAG_CONTROLS: { key: keyof TrialTrackerAssumptions; label: string; min: number; max: number }[] = [
  { key: 'pcToFilingMonths', label: 'PC → US filing', min: 0, max: 36 },
  { key: 'euFilingOffsetMonths', label: 'EU filing offset', min: 0, max: 24 },
  { key: 'standardReviewMonths', label: 'US review (std)', min: 1, max: 36 },
  { key: 'priorityReviewMonths', label: 'US review (priority)', min: 1, max: 24 },
  { key: 'euReviewMonths', label: 'EU review', min: 1, max: 36 },
];

const TrackerTimeline: React.FC<Props> = ({
  trials, showGroupHeaders, assumptions, onBumpAssumption, onSelect, priorityOverrides, onTogglePriority, onFilterBySponsor,
}) => {
  const [pxPerMonth, setPxPerMonth] = useState(PX_PER_MONTH_DEFAULT);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledToTodayRef = useRef(false);

  const rows = useMemo(() => {
    // Keep every trial from the table — undated rows still appear with a placeholder track.
    return trials.map((t) => {
      const designations = t.designations || [];
      const isPriority = priorityOverrides[t.nct_id]
        ?? designations.some((d) => ['BTD', 'ACCEL', 'PRIORITY', 'CN_PRIORITY'].includes(d.code));
      const m = computeTrackerMilestones(t, assumptions, isPriority);
      return { trial: t, milestones: m, isPriority };
    });
  }, [trials, assumptions, priorityOverrides]);

  const { rangeStart, rangeEnd } = useMemo(() => {
    const dates: Date[] = [];
    rows.forEach((r) => {
      [r.milestones.start, r.milestones.primaryCompletion, r.milestones.studyCompletion, r.milestones.usApproval, r.milestones.euApproval]
        .forEach((d) => { if (d) dates.push(d); });
    });
    if (dates.length === 0) {
      const now = new Date();
      return { rangeStart: now, rangeEnd: new Date(now.getFullYear() + 2, 0, 1) };
    }
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    min.setMonth(min.getMonth() - 1);
    max.setMonth(max.getMonth() + 2);
    return { rangeStart: min, rangeEnd: max };
  }, [rows]);

  const totalMonths = Math.max(1, Math.ceil(monthsBetween(rangeStart, rangeEnd)));
  const trackWidth = totalMonths * pxPerMonth;
  const now = new Date();
  const nowLeft = Math.max(0, monthsBetween(rangeStart, now)) * pxPerMonth;

  // Reset so a fresh load / filter change can re-center on today once layout is ready.
  useEffect(() => {
    scrolledToTodayRef.current = false;
  }, [trials, pxPerMonth, rangeStart, rangeEnd]);

  useEffect(() => {
    if (scrolledToTodayRef.current) return;
    const el = scrollRef.current;
    if (!el || rows.length === 0) return;

    const scrollToToday = () => {
      const viewport = el.clientWidth;
      if (viewport <= 0) return false;
      // Place today's marker near the middle of the visible track (right of sticky labels).
      const target = LABEL_COL_WIDTH + nowLeft - Math.max(80, (viewport - LABEL_COL_WIDTH) / 2);
      el.scrollLeft = Math.max(0, target);
      scrolledToTodayRef.current = true;
      return true;
    };

    if (scrollToToday()) return;
    const id = requestAnimationFrame(() => { scrollToToday(); });
    return () => cancelAnimationFrame(id);
  }, [rows.length, nowLeft, trackWidth, pxPerMonth]);

  const years = useMemo(() => {
    const out: { label: string; left: number }[] = [];
    const startYear = rangeStart.getFullYear();
    const endYear = rangeEnd.getFullYear();
    for (let y = startYear; y <= endYear; y++) {
      const left = monthsBetween(rangeStart, new Date(y, 0, 1)) * pxPerMonth;
      if (left >= 0 && left <= trackWidth) out.push({ label: String(y), left });
    }
    return out;
  }, [rangeStart, rangeEnd, pxPerMonth, trackWidth]);

  const leftOf = (d: Date | null) => (d ? monthsBetween(rangeStart, d) * pxPerMonth : null);

  return (
    <div className="flex-1 flex flex-col min-h-0 px-5 pt-2.5 pb-3">
      <div className="flex-none flex items-center gap-2 flex-wrap px-2.5 py-2 mb-2 bg-tracker-bg border border-tracker-line rounded-lg">
        <span className="text-[10px] font-bold tracking-wide text-tracker-muted flex-none">PROJECTION ASSUMPTIONS</span>
        <div className="ml-auto flex items-center gap-1.5 flex-none">
          <span className="text-[10px] font-bold tracking-wide text-tracker-muted">SCALE</span>
          <div className="flex gap-1">
            {[12, 22, 40].map((z) => (
              <span
                key={z}
                onClick={() => setPxPerMonth(z)}
                className={`text-[10.5px] font-semibold px-2 py-1 rounded-md border cursor-pointer ${pxPerMonth === z ? 'bg-tracker-accent border-tracker-accent text-white' : 'bg-white border-tracker-line text-tracker-muted'}`}
              >
                {z === 12 ? 'Compact' : z === 22 ? 'Default' : 'Wide'}
              </span>
            ))}
          </div>
        </div>
        {LAG_CONTROLS.map((c) => (
          <div key={c.key} className="flex items-center gap-1.5 bg-white border border-tracker-line rounded-md pl-2 pr-1 py-0.5">
            <span className="text-[10.5px] text-tracker-muted whitespace-nowrap">{c.label}</span>
            <span className="text-[11px] font-bold font-plex text-tracker-ink min-w-[26px] text-right">{assumptions[c.key]}</span>
            <div className="flex gap-0.5">
              <button
                onClick={() => onBumpAssumption(c.key, -1)}
                className="w-4.5 h-4.5 rounded bg-tracker-line3 text-tracker-muted text-xs leading-none flex items-center justify-center w-[18px] h-[18px]"
              >
                −
              </button>
              <button
                onClick={() => onBumpAssumption(c.key, 1)}
                className="w-4.5 h-4.5 rounded bg-tracker-line3 text-tracker-muted text-xs leading-none flex items-center justify-center w-[18px] h-[18px]"
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto min-h-0">
        <div style={{ minWidth: 'max-content' }}>
          <div
            className="sticky top-0 z-[3] grid gap-2.5 items-end pb-1.5 border-b border-tracker-line bg-tracker-bg"
            style={{ gridTemplateColumns: `${LABEL_COL_WIDTH}px ${trackWidth}px` }}
          >
            <div className="sticky left-0 z-[4] bg-tracker-bg text-[10px] font-bold tracking-wide text-tracker-muted">
              {rows.length} trial{rows.length === 1 ? '' : 's'}
            </div>
            <div className="relative h-4">
              {years.map((y) => (
                <span key={y.label} className="absolute -translate-x-1/2 text-[9.5px] font-semibold font-plex text-tracker-muted" style={{ left: y.left }}>
                  {y.label}
                </span>
              ))}
            </div>
          </div>

          <div className="pt-1">
            {rows.length === 0 && (
              <div className="py-12 text-center">
                <div className="text-sm font-semibold text-tracker-ink">No trials match these filters</div>
                <div className="text-xs text-tracker-muted mt-1">Clear the search or widen phase / source.</div>
              </div>
            )}
            {rows.map((r, i) => {
              const t = r.trial;
              const m = r.milestones;
              const status = statusColorClasses(t.status);
              const registry = registryForNctId(t.nct_id);
              const startLeft = leftOf(m.start) ?? 0;
              const pcLeft = leftOf(m.primaryCompletion);
              const scLeft = leftOf(m.studyCompletion);
              const barEnd = scLeft ?? pcLeft ?? startLeft;
              const enrollEnd = pcLeft ?? barEnd;
              const groupName = t.indication_name || 'Other / unspecified';
              const prevGroupName = i > 0 ? (rows[i - 1].trial.indication_name || 'Other / unspecified') : null;
              const showHeader = showGroupHeaders && groupName !== prevGroupName;
              // Feed explodes one NCT across indications — keys must include indication.
              const rowKey = `${t.indication_id ?? 'x'}:${t.nct_id}:${i}`;
              return (
                <React.Fragment key={rowKey}>
                {showHeader && (
                  <div className="flex items-center gap-2 pt-2.5 pb-1 sticky left-0" style={{ width: LABEL_COL_WIDTH + trackWidth }}>
                    <span className="text-[11px] font-bold text-tracker-muted">{groupName}</span>
                    <div className="flex-1 h-px bg-tracker-line" />
                  </div>
                )}
                <div
                  onClick={() => onSelect(t.nct_id)}
                  title={t.full_title || t.trial_name || t.nct_id}
                  className="grid gap-2.5 items-center py-0.5 border-b border-tracker-line3 cursor-pointer hover:bg-tracker-bg"
                  style={{ gridTemplateColumns: `${LABEL_COL_WIDTH}px ${trackWidth}px` }}
                >
                  <div className="sticky left-0 z-[2] bg-white min-w-0 pl-1.5 pr-2 flex items-center gap-1.5 h-[34px]">
                    <span className={`w-1.5 h-1.5 rounded-full flex-none ${registry.dot}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[12.5px] font-semibold truncate">{t.trial_name || t.full_title || t.nct_id}</span>
                      </div>
                      <div className="text-[9.5px] text-tracker-muted font-plex truncate">
                        {t.nct_id}
                        {t.sponsor && onFilterBySponsor ? (
                          <>
                            {' · '}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onFilterBySponsor(t.sponsor!);
                              }}
                              className="text-sky-700 hover:underline font-sans font-semibold"
                            >
                              {t.sponsor}
                            </button>
                          </>
                        ) : (
                          <>{t.sponsor ? ` · ${t.sponsor}` : ' · —'}</>
                        )}
                      </div>
                    </div>
                    <span className={`flex-none text-[9.5px] font-bold px-1.5 py-0.5 rounded border ${status.bg} ${status.fg} border-transparent`}>
                      {phaseShortLabel(t.phase || undefined)}
                    </span>
                    <span
                      onClick={(e) => onTogglePriority(t.nct_id, e)}
                      title="Toggle FDA priority review for this trial"
                      className={`flex-none text-[9px] font-bold px-1.5 py-0.5 rounded-full cursor-pointer ${r.isPriority ? 'bg-tracker-resultsBg text-tracker-resultsFg border border-tracker-resultsBg' : 'bg-tracker-line3 text-tracker-muted border border-tracker-line'}`}
                    >
                      {r.isPriority ? 'PR' : 'std'}
                    </span>
                  </div>
                  <div className="relative h-[38px]" style={{ width: trackWidth }}>
                    {i === 0 && (
                      <div className="absolute top-0 bottom-0 border-l border-dashed border-tracker-priority" style={{ left: nowLeft }} />
                    )}
                    {!m.start && !m.primaryCompletion ? (
                      // Anchor near "today" so the note stays visible after scroll-to-today
                      // (absolute left:0 sits off-screen once the viewport is centered on now).
                      <div
                        className="absolute inset-y-0 flex items-center text-[10px] text-tracker-muted italic whitespace-nowrap px-1"
                        style={{ left: Math.max(8, nowLeft - 4) }}
                      >
                        No start / primary completion dates
                      </div>
                    ) : (
                      <>
                        <div
                          className="absolute rounded-full bg-tracker-line3 border border-tracker-line"
                          style={{ left: startLeft, width: Math.max(2, barEnd - startLeft), top: 12, height: 8 }}
                        />
                        <div
                          className="absolute rounded-full bg-tracker-enrollBar"
                          style={{ left: startLeft, width: Math.max(2, enrollEnd - startLeft), top: 12, height: 8 }}
                        />
                        {pcLeft != null && (
                          <div
                            title="Primary completion"
                            className="absolute bg-white border-2 border-tracker-muted2"
                            style={{ left: pcLeft - 4, top: 11, width: 8, height: 8, transform: 'rotate(45deg)' }}
                          />
                        )}
                        {!m.isCompleted && m.usFiling && m.usApproval && (
                          <>
                            <div className="absolute border-t border-dotted border-tracker-usDot" style={{ left: pcLeft ?? startLeft, width: (leftOf(m.usFiling) ?? 0) - (pcLeft ?? startLeft), top: 4 }} />
                            <div title={`US filing: ${m.usFiling.toDateString()}`} className="absolute bg-white border-2 border-tracker-usDot rounded-full" style={{ left: (leftOf(m.usFiling) ?? 0) - 4, top: -4, width: 8, height: 8 }} />
                            <div title={`US approval: ${m.usApproval.toDateString()}`} className="absolute bg-tracker-usDot border-2 border-white rounded-full" style={{ left: (leftOf(m.usApproval) ?? 0) - 4, top: -4, width: 8, height: 8 }} />
                          </>
                        )}
                        {!m.isCompleted && m.euFiling && m.euApproval && (
                          <>
                            <div className="absolute border-t border-dotted border-tracker-euDot" style={{ left: pcLeft ?? startLeft, width: (leftOf(m.euFiling) ?? 0) - (pcLeft ?? startLeft), bottom: 4 }} />
                            <div title={`EU filing: ${m.euFiling.toDateString()}`} className="absolute bg-white border-2 border-tracker-euDot rounded-full" style={{ left: (leftOf(m.euFiling) ?? 0) - 4, bottom: -4, width: 8, height: 8 }} />
                            <div title={`EU approval: ${m.euApproval.toDateString()}`} className="absolute bg-tracker-euDot border-2 border-white rounded-full" style={{ left: (leftOf(m.euApproval) ?? 0) - 4, bottom: -4, width: 8, height: 8 }} />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-none flex items-center flex-wrap gap-x-3.5 gap-y-1.5 pt-2 mt-0 border-t border-tracker-line text-[10.5px] text-tracker-muted">
        <span className="flex items-center gap-1.5"><span className="w-5 h-2 rounded bg-tracker-enrollBar" />Enrolling → LPI</span>
        <span className="flex items-center gap-1.5"><span className="w-5 h-2 rounded bg-tracker-line3 border border-tracker-line" />Follow-up → completion</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-white border-2 border-tracker-muted2" style={{ transform: 'rotate(45deg)' }} />Primary completion</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-tracker-usDot" /><span className="text-tracker-usDot font-semibold">US</span> filing (hollow) / approval (filled)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-tracker-euDot" /><span className="text-tracker-euDot font-semibold">EU</span> filing (hollow) / approval (filled)</span>
        <span className="flex items-center gap-1.5"><span className="w-0 h-3 border-l border-dashed border-tracker-priority" />Today</span>
      </div>
      <div className="flex-none text-[10px] text-tracker-muted mt-1">
        Designation legend: {Object.entries(DESIGNATION_META).slice(0, 4).map(([code, d]) => `${d.short} (${code})`).join(' · ')}…
      </div>
    </div>
  );
};

export default TrackerTimeline;
